import React, { useState, useRef } from 'react';
import { Upload, X, File, FileText, Image, Video, Loader, Eye, Link as LinkIcon, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import FileViewer from './FileViewer';

const ProofFileUpload = ({
    proofFiles = [],
    onProofFilesChange,
    proofUrl,
    onProofUrlChange,
    error,
    isSubmitting,
    readOnly = false // New prop
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragActive, setIsDragActive] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const fileInputRef = useRef(null);

    const getFileIcon = (fileType) => {
        if (fileType?.startsWith('image/')) {
            return <Image className="w-5 h-5 text-blue-500" />;
        } else if (fileType?.startsWith('video/')) {
            return <Video className="w-5 h-5 text-purple-500" />;
        } else if (fileType === 'application/pdf') {
            return <FileText className="w-5 h-5 text-red-500" />;
        } else {
            return <File className="w-5 h-5 text-gray-500" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleFiles = async (files) => {
        if (files.length === 0) return;

        // Validate file count (max 3 proof files)
        if (proofFiles.length + files.length > 3) {
            toast.error('Maximum 3 proof files allowed');
            return;
        }

        // Validate file sizes (max 10MB each)
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error(`Some files exceed the 10 MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });

            const token = localStorage.getItem('token');
            const response = await api.post('/files/upload-proof', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                },
            });

            if (response.data.success) {
                const newProofFiles = [...proofFiles, ...response.data.files];
                onProofFilesChange(newProofFiles);
                toast.success(`${response.data.files.length} file(s) uploaded successfully`);
            }
        } catch (error) {
            console.error('Proof file upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload files. Please try again.');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = ''; // Reset input
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!uploading && proofFiles.length < 3 && !readOnly) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        if (uploading || proofFiles.length >= 3 || readOnly) return;

        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleRemoveFile = (fileToRemove) => {
        const newProofFiles = proofFiles.filter(f => f.file_key !== fileToRemove.file_key);
        onProofFilesChange(newProofFiles);
        toast.success('File removed');
    };

    return (
        <div className={`space-y-4 pt-4 border-t border-gray-100 ${error ? 'bg-red-50 p-4 rounded-lg border border-red-200' : ''}`}>
            <div className="flex items-center gap-2 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                    <Paperclip className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Upload Proof of Completion</h3>
            </div>

            {!readOnly && (
                <p className="text-sm text-gray-600 mb-2">
                    Upload your completed certificate, screenshot, or documentation here.
                </p>
            )}

            {/* Drag and Drop Area - Hide if readOnly */}
            {!readOnly && (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !uploading && proofFiles.length < 3 && !readOnly && fileInputRef.current?.click()}
                    className={`
                        relative border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer flex flex-col items-center gap-3 text-center
                        ${isDragActive
                            ? 'border-primary bg-primary/5 scale-[0.99]'
                            : error
                                ? 'border-red-300 bg-white hover:bg-neutral-50'
                                : 'border-gray-300 hover:border-primary/50 hover:bg-gray-50'
                        }
                        ${(uploading || proofFiles.length >= 3) ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                    <div className={`p-4 rounded-full ${isDragActive ? 'bg-white text-primary shadow-sm' : 'bg-gray-100 text-gray-400'}`}>
                        <Upload className="w-8 h-8" />
                    </div>
                    <div>
                        <p className="text-base font-semibold text-gray-800">
                            {uploading ? 'Uploading files...' : 'Click to browse or drag & drop'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Accepted formats: PDF, JPG, PNG, DOC (Max 10MB)
                        </p>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={handleFileSelect}
                        disabled={uploading || proofFiles.length >= 3 || isSubmitting || readOnly}
                        className="hidden"
                    />
                </div>
            )}

            {error && <p className="text-xs text-red-600 font-medium px-2">{error}</p>}

            {/* Upload Progress */}
            {uploading && (
                <div className="space-y-2 px-2">
                    <div className="flex items-center gap-2">
                        <Loader className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm text-gray-600">Uploading... {uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                            className="bg-primary h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Uploaded Files List */}
            {proofFiles.length > 0 ? (
                <div className="space-y-2 mt-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">
                        Attached Files ({proofFiles.length})
                    </p>
                    <div className="grid gap-2">
                        {proofFiles.map((file, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg group hover:border-primary/30 hover:shadow-sm transition-all"
                            >
                                <div
                                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                                    onClick={() => setPreviewFile(file)}
                                >
                                    <div className="p-2 bg-gray-50 rounded-lg">
                                        {getFileIcon(file.file_type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate group-hover:text-primary transition-colors">
                                            {file.file_name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatFileSize(file.file_size)}
                                        </p>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity px-2">
                                        <span className="text-xs text-primary font-medium hover:underline">View</span>
                                    </div>
                                </div>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isSubmitting) handleRemoveFile(file);
                                        }}
                                        disabled={isSubmitting}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Remove file"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                readOnly && (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        <p>No proof attached.</p>
                    </div>
                )
            )}

            {/* Optional URL Input if passed and NOT readOnly */}
            {onProofUrlChange && !readOnly && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">OR</span>
                        <span className="h-px bg-gray-200 flex-1"></span>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        Provide a Link (Optional)
                    </label>
                    <input
                        type="url"
                        value={proofUrl || ''}
                        onChange={(e) => onProofUrlChange(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="https://example.com/proof"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                </div>
            )}


            {/* File Viewer Modal */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </div>
    );
};

export default ProofFileUpload;

